import { SxProps, Theme } from '@mui/material/styles';
import { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { muiTheme } from '../../../theme';
import { isInSameWeek } from '../../../utils';
import { useRangeSelection } from '../../../hooks/useRangeSelection';
import { CustomPickersDay } from './DatePicker.styles';
import { createHoverHandlers, createRangeStyle, getRangeColors } from './utils';

// Week mode day renderer
export const createWeekDayComponent =
  (selected: Date | null, hovered: Date | null, setHovered: (date: Date | null) => void) =>
  (dayProps: PickersDayProps<Date>) => {
    const hoverHandlers = createHoverHandlers(setHovered, dayProps.day);
    return (
      <CustomPickersDay
        {...dayProps}
        sx={{ ...dayProps.sx, px: 2.5 } as SxProps<Theme>}
        disableMargin
        isSelected={isInSameWeek(dayProps.day, selected)}
        isHovered={isInSameWeek(dayProps.day, hovered)}
        {...hoverHandlers}
      />
    );
  };

// Range mode day renderer
export const createRangeDayComponent =
  (
    rangeColors: ReturnType<typeof getRangeColors>,
    rangeState: ReturnType<typeof useRangeSelection>,
    hovered: Date | null,
    setHovered: (date: Date | null) => void,
  ) =>
  (dayProps: PickersDayProps<Date>) => {
    const {
      isDateDisabled,
      isDateInRange,
      isDateRangeStart,
      isDateRangeEnd,
      isDateInSelectableWindow,
      isValidRange,
    } = rangeState;

    const day = dayProps.day;
    const isDisabled = isDateDisabled(day);
    const isInRange = isDateInRange(day);
    const isRangeStart = isDateRangeStart(day);
    const isRangeEnd = isDateRangeEnd(day);
    const hoverHandlers = createHoverHandlers(setHovered, day);

    const dayStyles: SxProps<Theme> = {
      ...dayProps.sx,
      px: 2.5,
      ...(isDisabled && {
        backgroundColor: 'transparent',
        color: muiTheme.palette.text.disabled,
        cursor: 'not-allowed',
        opacity: 0.4,
        '&:hover': { backgroundColor: 'transparent' },
      }),
      ...(isDateInSelectableWindow(day) &&
        !isDisabled &&
        !isInRange &&
        !isRangeStart &&
        !isRangeEnd && {
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: rangeColors.hover },
        }),
      ...(isInRange && {
        backgroundColor: isValidRange ? rangeColors.background : rangeColors.invalid,
        '&:hover': { backgroundColor: isValidRange ? rangeColors.hover : rangeColors.invalid },
      }),
      ...(isRangeStart && {
        ...createRangeStyle(isValidRange, rangeColors),
        color: rangeColors.selectedText,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      }),
      ...(isRangeEnd &&
        !isRangeStart && {
          ...createRangeStyle(isValidRange, rangeColors),
          color: rangeColors.selectedText,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }),
    };

    return (
      <CustomPickersDay
        {...dayProps}
        sx={dayStyles}
        disableMargin
        disabled={isDisabled}
        isSelected={isRangeStart || isRangeEnd}
        isHovered={isInSameWeek(day, hovered)}
        {...hoverHandlers}
      />
    );
  };
